import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'

const ConfettiContext = createContext()

export const useConfetti = () => {
  const context = useContext(ConfettiContext)
  if (!context) {
    throw new Error('useConfetti must be used within a ConfettiProvider')
  }
  return context
}

// Generate random number in range
const random = (min, max) => Math.random() * (max - min) + min
const randomInt = (min, max) => Math.floor(random(min, max))

// Confetti Particle Component - Smooth parabolic physics
const ConfettiParticle = ({ color, size, startX, startY, xVelocity, yVelocity, rotation, duration, shape, delay }) => {
  const shapeStyles = {
    square: { borderRadius: '2px' },
    circle: { borderRadius: '50%' },
    rectangle: { borderRadius: '1px' },
  }

  // Pre-calculate many keyframe points for smooth motion
  const keyframeCount = 30
  const opacityFrames = []
  const xFrames = []
  const yFrames = []
  const rotateFrames = []
  const timeFrames = []

  for (let i = 0; i < keyframeCount; i++) {
    const t = i / (keyframeCount - 1) // 0 to 1
    timeFrames.push(t)

    // Smooth opacity fade at the end
    opacityFrames.push(t < 0.85 ? 1 : 1 - (t - 0.85) / 0.15)

    // Linear horizontal motion
    xFrames.push(xVelocity * t)

    // Parabolic arc with gravity: shoot up then come down
    const parabola = Math.sin(t * Math.PI) * Math.abs(yVelocity) * 0.5
    const gravityFall = (t * t) * 300
    yFrames.push(-parabola + gravityFall)

    // Smooth rotation
    rotateFrames.push(rotation * t)
  }

  return (
    <motion.div
      className="absolute pointer-events-none will-change-transform"
      style={{
        left: startX,
        top: startY,
        width: shape === 'rectangle' ? size * 0.4 : size,
        height: size,
        backgroundColor: color,
        ...shapeStyles[shape],
      }}
      initial={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
      }}
      animate={{
        opacity: opacityFrames,
        x: xFrames,
        y: yFrames,
        rotate: rotateFrames,
      }}
      transition={{
        duration: duration,
        delay: delay,
        times: timeFrames,
        ease: 'linear',
      }}
    />
  )
}

const MAX_CONFETTI_DURATION = 2000 // ~1.8 seconds + buffer (2.25x faster overall)

// Color palettes for different rarity levels
const COLOR_PALETTES = {
  generic: ['#f1c40f', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12', '#1abc9c'],
  common: ['#f5f5f5', '#d3d3d3', '#a9a9a9', '#808080', '#696969'],
  uncommon: ['#90ee90', '#7bc67b', '#3cb371', '#2d8659', '#1f5a35'],
  rare: ['#add8e6', '#87ceeb', '#4a90e2', '#2c5aa0', '#1a3a5c'],
  epic: ['#dda0dd', '#ba55d3', '#9932cc', '#7b3fc7', '#6300a3'],
  legendary: ['#ffff99', '#ffff00', '#ffd700', '#ffa500', '#ff8c00'],
}

export const ConfettiProvider = ({ children }) => {
  const [triggerIds, setTriggerIds] = useState([])

  const trigger = useCallback((rarity = 'generic') => {
    const newId = Date.now() + Math.random()
    setTriggerIds(prev => [...prev, { id: newId, rarity }])

    // Remove the trigger after animation completes so component unmounts
    setTimeout(() => {
      setTriggerIds(prev => prev.filter(item => item.id !== newId))
    }, MAX_CONFETTI_DURATION)
  }, [])

  return (
    <ConfettiContext.Provider value={{ trigger }}>
      {children}

      {/* Render all active confetti effects */}
      {triggerIds.map(item => (
        <ConfettiEffect key={item.id} rarity={item.rarity} />
      ))}
    </ConfettiContext.Provider>
  )
}

// Separate component to handle confetti rendering
const ConfettiEffect = ({ rarity = 'generic' }) => {
  const [particles, setParticles] = useState([])

  useEffect(() => {
    const colors = COLOR_PALETTES[rarity] || COLOR_PALETTES.generic
    const particleCount = 80
    const halfCount = Math.floor(particleCount / 2)
    const newParticles = []
    const maxDuration = 5
    const screenHeight = window.innerHeight
    const screenWidth = window.innerWidth

    // Left stream - bottom-left shooting diagonally up and right (65-70 degrees)
    for (let i = 0; i < halfCount; i++) {
      const duration = random(1.4, 1.8)
      newParticles.push({
        id: `left-${Date.now()}-${i}`,
        color: colors[randomInt(0, colors.length)],
        size: random(8, 16),
        startX: random(0, screenWidth * 0.2),
        startY: screenHeight,
        xVelocity: random(900, 1100),
        yVelocity: random(-1500, -1150),
        rotation: random(0, 720),
        duration: duration,
        shape: ['square', 'circle', 'rectangle'][randomInt(0, 3)],
        delay: random(0, 0.15),
      })
    }

    // Right stream - bottom-right shooting diagonally up and left (65-70 degrees)
    for (let i = 0; i < halfCount; i++) {
      const duration = random(1.4, 1.8)
      newParticles.push({
        id: `right-${Date.now()}-${i}`,
        color: colors[randomInt(0, colors.length)],
        size: random(8, 16),
        startX: random(screenWidth * 0.8, screenWidth),
        startY: screenHeight,
        xVelocity: random(-1100, -900),
        yVelocity: random(-1500, -1150),
        rotation: random(0, 720),
        duration: duration,
        shape: ['square', 'circle', 'rectangle'][randomInt(0, 3)],
        delay: random(0, 0.15),
      })
    }

    setParticles(newParticles)
  }, [rarity])

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {particles.map((particle) => (
        <ConfettiParticle key={particle.id} {...particle} />
      ))}
    </div>
  )
}

export default ConfettiContext
